/**
 * TaskBoardHeader Component
 *
 * Restructured header with clear visual hierarchy:
 * - Scope as prominent heading (not dropdown)
 * - Context line showing task count
 * - Compact search (icon expands)
 * - Utilities grouped in menu
 * - Reduced primary action size
 *
 * Hierarchy: Scope Title > Context Line > Primary Action > Utilities
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Input, Button, Badge, Popover, Checkbox } from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  UserOutlined,
  TeamOutlined,
  FilterOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useTaskExport } from '../../hooks/useTaskExport';
import { space, text } from '../../configs/designTokens';
import { SEARCH_FIELD_OPTIONS } from '../../configs/task.config';
import '../../styles/index.scss';

const TaskBoardHeader = ({
  windowWidth,
  showMyTasks,
  setShowMyTasks,
  searchValue,
  onSearchChange,
  onSearchClear,
  searchFields,
  onSearchFieldsChange,
  onBulkImportClick,
  onCreateTaskClick,
  level,
  setViewChanged,
  // Filter integration
  showFilters,
  setShowFilters,
  activeFilterCount,
  // Context data
  taskCount = 0,
  overdueCount = 0,
  // Export
  getExportWhere,
}) => {
  const isMobile = windowWidth < 768;
  const { exportTasks, isExporting } = useTaskExport();

  const handleExport = () => {
    const where = getExportWhere ? getExportWhere() : undefined;
    exportTasks(where);
  };

  const handleScopeToggle = () => {
    setShowMyTasks(!showMyTasks);
    setViewChanged(true);
  };

  const searchFieldsPopover = (
    <div style={{ minWidth: 160 }}>
      <div style={{ marginBottom: space[2], fontSize: 12, color: text.muted }}>
        Search in
      </div>
      <Checkbox.Group
        options={SEARCH_FIELD_OPTIONS}
        value={searchFields}
        onChange={onSearchFieldsChange}
        style={{ display: 'flex', flexDirection: 'column' }}
      />
    </div>
  );

  const searchFieldsSuffix = (
    <Popover
      content={searchFieldsPopover}
      trigger="click"
      placement="bottomRight"
      title="Search fields"
    >
      <DownOutlined
        style={{ color: text.muted, cursor: 'pointer' }}
        aria-label="Choose search fields"
      />
    </Popover>
  );

  // Build context line
  const contextParts = [];
  if (taskCount > 0) {
    contextParts.push(`${taskCount} task${taskCount !== 1 ? 's' : ''}`);
  }
  if (overdueCount > 0) {
    contextParts.push(`${overdueCount} overdue`);
  }
  const contextLine = contextParts.join(' · ');

  return (
    <div className="task-board-header-modern">
      {/* Mobile Layout */}
      {isMobile ? (
        <div className="task-board-header__mobile">
          {/* Top row: Title + Actions */}
          <div className="task-board-header__mobile-top">
            <div className="task-board-header__title-group">
              <h1 className="task-board-header__title">
                {showMyTasks ? 'My Tasks' : 'All Tasks'}
              </h1>
              {contextLine && (
                <span className="task-board-header__context">{contextLine}</span>
              )}
            </div>
            <div className="task-board-header__mobile-actions">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="task-board-header__add-btn"
                onClick={onCreateTaskClick}
              />
            </div>
          </div>

          {/* Search row */}
          <div className="task-board-header__mobile-search-row">
            <Input
              className="task-board-search-mobile"
              placeholder="Search tasks..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              prefix={<SearchOutlined style={{ color: text.muted }} />}
              suffix={searchFieldsSuffix}
              allowClear
              onClear={onSearchClear}
            />
            <button
              className={`task-board-header__icon-btn ${showFilters ? 'active' : ''} ${activeFilterCount > 0 ? 'has-badge' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              aria-label="Filters"
            >
              <FilterOutlined />
              {activeFilterCount > 0 && (
                <span className="task-board-header__badge-dot" />
              )}
            </button>
          </div>

          {/* Bottom row: Scope toggle */}
          <div className="task-board-header__mobile-bottom">
            <button
              className={`task-board-header__scope-toggle ${!showMyTasks ? 'task-board-header__scope-toggle--all' : ''}`}
              onClick={handleScopeToggle}
            >
              <span className={`task-board-header__scope-option ${showMyTasks ? 'active' : ''}`}>
                <UserOutlined /> Mine
              </span>
              <span className={`task-board-header__scope-option ${!showMyTasks ? 'active' : ''}`}>
                <TeamOutlined /> All
              </span>
            </button>

          </div>
        </div>
      ) : (
        /* Desktop Layout */
        <div className="task-board-header__desktop">
          {/* Top: Title + Context */}
          <div className="task-board-header__title-bar">
            <div className="task-board-header__title-row">
              <h1 className="task-board-header__title">
                {showMyTasks ? 'My Tasks' : 'All Tasks'}
              </h1>
              <button
                className="task-board-header__scope-switch"
                onClick={handleScopeToggle}
                aria-label={`Switch to ${showMyTasks ? 'All Tasks' : 'My Tasks'}`}
              >
                {showMyTasks ? <TeamOutlined /> : <UserOutlined />}
              </button>
            </div>
            {contextLine && (
              <span className="task-board-header__context">{contextLine}</span>
            )}
          </div>

          {/* Action Row: wide search + filter/import/export/new */}
          <div className="task-board-header__action-row">
            <Input
              className="task-board-search task-board-header__search-input"
              placeholder="Search tasks..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              prefix={<SearchOutlined style={{ color: text.muted }} />}
              suffix={searchFieldsSuffix}
              allowClear
              onClear={onSearchClear}
            />

            <Button
              className={`task-filters__toggle ${showFilters ? 'task-filters__toggle--active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              icon={<FilterOutlined />}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {activeFilterCount > 0 && (
                <Badge count={activeFilterCount} className="task-filters__badge" />
              )}
            </Button>

            {level === 'PLATFORM' && (
              <>
                <Button
                  className="task-filters__toggle"
                  icon={<UploadOutlined />}
                  onClick={onBulkImportClick}
                >
                  Bulk Import
                </Button>
                <Button
                  className="task-filters__toggle"
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled={isExporting}
                >
                  {isExporting ? 'Exporting...' : 'Export Tasks'}
                </Button>
              </>
            )}

            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="task-board-header__primary-btn"
              onClick={onCreateTaskClick}
            >
              New
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

TaskBoardHeader.propTypes = {
  windowWidth: PropTypes.number.isRequired,
  showMyTasks: PropTypes.bool.isRequired,
  setShowMyTasks: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  onSearchClear: PropTypes.func.isRequired,
  searchFields: PropTypes.arrayOf(PropTypes.string),
  onSearchFieldsChange: PropTypes.func,
  onBulkImportClick: PropTypes.func.isRequired,
  onCreateTaskClick: PropTypes.func.isRequired,
  level: PropTypes.string,
  setViewChanged: PropTypes.func.isRequired,
  // Filter integration props
  showFilters: PropTypes.bool,
  setShowFilters: PropTypes.func,
  activeFilterCount: PropTypes.number,
  // Context data
  taskCount: PropTypes.number,
  overdueCount: PropTypes.number,
  // Export
  getExportWhere: PropTypes.func,
};

TaskBoardHeader.defaultProps = {
  searchValue: '',
  searchFields: SEARCH_FIELD_OPTIONS.map((option) => option.value),
  onSearchFieldsChange: () => {},
  level: '',
  showFilters: false,
  setShowFilters: () => {},
  activeFilterCount: 0,
  taskCount: 0,
  overdueCount: 0,
  getExportWhere: undefined,
};

export default TaskBoardHeader;
